/**
 * Database Service - Frontend client for backend API
 * 
 * This service communicates with the Node.js backend for all data operations.
 * Data is persisted on the server in JSON files.
 */

// Types
export interface User {
  id: string;
  name: string;
  email: string;
  phone: string;
  company: string;
  avatar: string;
  createdAt: string;
  settings: {
    theme: string;
    language: string;
    notifications: {
      email: boolean;
      push: boolean;
      sms: boolean;
      flowErrors: boolean;
      flowSuccess: boolean;
      updates: boolean;
    };
  };
}

export interface Flow {
  id: string;
  userId?: string;
  name: string;
  description?: string;
  nodes: any[];
  edges: any[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  stats: {
    executions: number;
    successRate: number;
    lastRun: string | null;
  };
}

export interface Message {
  id: string;
  flowId?: string;
  contactId?: string;
  from: string;
  fromName?: string;
  body: string;
  type: 'sent' | 'received';
  content?: string;
  timestamp: string;
  status?: 'pending' | 'sent' | 'delivered' | 'read' | 'failed';
  isGroup?: boolean;
  groupName?: string;
}

export interface Contact {
  id: string;
  name: string;
  phone?: string;
  number?: string;
  tags?: string[];
  createdAt?: string;
  lastInteraction?: string;
  lastSeen?: string;
  attributes?: Record<string, any>;
  isGroup?: boolean;
}

export interface Group {
  id: string;
  name: string;
  description?: string;
  members?: string[];
  admins?: string[];
  participantCount?: number;
  createdAt?: string;
}

export interface Log {
  id: string;
  timestamp: string;
  level: 'info' | 'warning' | 'error';
  flowId?: string;
  message: string;
  details?: any;
}

export interface Analytics {
  daily: Array<{
    date: string;
    messagesProcessed: number;
    flowExecutions: number;
    newContacts: number;
    errors: number;
  }>;
  flowStats: Record<string, {
    totalExecutions: number;
    successCount: number;
    errorCount: number;
    averageResponseTime: number;
  }>;
}

class DatabaseService {
  private readonly STORAGE_KEY = 'whatsapp-flow-db';
  private readonly API_BASE = '/api';
  private localCache: any = null;

  constructor() {
    this.loadLocalCache();
  }

  // Load local cache for offline/fallback
  private loadLocalCache() {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        this.localCache = JSON.parse(stored);
      } else {
        this.localCache = {
          users: [],
          flows: [],
          messages: [],
          contacts: [],
          groups: [],
          logs: [],
          analytics: { daily: [], flowStats: {} }
        };
      }
    } catch (error) {
      console.error('Error loading local cache:', error);
      this.localCache = {
        users: [],
        flows: [],
        messages: [],
        contacts: [],
        groups: [],
        logs: [],
        analytics: { daily: [], flowStats: {} }
      };
    }
  }

  private saveLocalCache() {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.localCache));
    } catch (error) {
      console.error('Error saving local cache:', error);
    }
  }

  private generateId(prefix: string): string {
    return `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // ============================================
  // USER OPERATIONS (local only for now)
  // ============================================

  getUser(userId: string): User | null {
    return this.localCache.users.find((u: User) => u.id === userId) || null;
  }

  updateUser(userId: string, updates: Partial<User>): User | null {
    const index = this.localCache.users.findIndex((u: User) => u.id === userId);
    if (index === -1) return null;
    
    this.localCache.users[index] = { ...this.localCache.users[index], ...updates };
    this.saveLocalCache();
    return this.localCache.users[index];
  }

  // ============================================
  // FLOW OPERATIONS (via backend API)
  // ============================================

  async getAllFlowsAsync(): Promise<Flow[]> {
    try {
      const response = await fetch(`${this.API_BASE}/flows`);
      const data = await response.json();
      if (data.success) {
        this.localCache.flows = data.flows;
        this.saveLocalCache();
        return data.flows;
      }
    } catch (error) {
      console.error('Error fetching flows:', error);
    }
    return this.localCache.flows || [];
  }

  // Sync version for compatibility
  getAllFlows(userId?: string): Flow[] {
    // Return cached flows, trigger async refresh
    this.getAllFlowsAsync();
    return this.localCache.flows || [];
  }

  getFlow(flowId: string): Flow | null {
    return this.localCache.flows?.find((f: Flow) => f.id === flowId) || null;
  }

  async createFlowAsync(flow: Omit<Flow, 'id' | 'createdAt' | 'updatedAt' | 'stats'>): Promise<Flow | null> {
    try {
      const response = await fetch(`${this.API_BASE}/flows`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(flow)
      });
      const data = await response.json();
      if (data.success) {
        this.localCache.flows.push(data.flow);
        this.saveLocalCache();
        return data.flow;
      }
    } catch (error) {
      console.error('Error creating flow:', error);
    }
    return null;
  }

  // Sync version for compatibility
  createFlow(flow: Omit<Flow, 'id' | 'createdAt' | 'updatedAt' | 'stats'>): Flow {
    const newFlow: Flow = {
      ...flow,
      id: this.generateId('flow'),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      stats: { executions: 0, successRate: 0, lastRun: null }
    };
    
    this.localCache.flows.push(newFlow);
    this.saveLocalCache();
    
    // Sync to backend
    this.createFlowAsync(flow);
    
    return newFlow;
  }

  async updateFlowAsync(flowId: string, updates: Partial<Flow>): Promise<Flow | null> {
    try {
      const response = await fetch(`${this.API_BASE}/flows/${flowId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      const data = await response.json();
      if (data.success) {
        const index = this.localCache.flows.findIndex((f: Flow) => f.id === flowId);
        if (index >= 0) {
          this.localCache.flows[index] = data.flow;
          this.saveLocalCache();
        }
        return data.flow;
      }
    } catch (error) {
      console.error('Error updating flow:', error);
    }
    return null;
  }

  updateFlow(flowId: string, updates: Partial<Flow>): Flow | null {
    const index = this.localCache.flows.findIndex((f: Flow) => f.id === flowId);
    if (index === -1) return null;
    
    this.localCache.flows[index] = {
      ...this.localCache.flows[index],
      ...updates,
      updatedAt: new Date().toISOString()
    };
    this.saveLocalCache();
    
    // Sync to backend
    this.updateFlowAsync(flowId, updates);
    
    return this.localCache.flows[index];
  }

  async deleteFlowAsync(flowId: string): Promise<boolean> {
    try {
      const response = await fetch(`/api/flows/${flowId}`, { method: 'DELETE' });
      const data = await response.json();
      return data.success;
    } catch (error) {
      console.error('Error deleting flow:', error);
      return false;
    }
  }

  deleteFlow(flowId: string): boolean {
    const index = this.localCache.flows.findIndex((f: Flow) => f.id === flowId);
    if (index === -1) return false;
    
    this.localCache.flows.splice(index, 1);
    this.saveLocalCache();
    
    // Sync to backend
    this.deleteFlowAsync(flowId);
    
    return true;
  }

  // ============================================
  // MESSAGE OPERATIONS
  // ============================================

  async getMessagesAsync(limit: number = 50): Promise<Message[]> {
    try {
      const response = await fetch(`/messages?limit=${limit}`);
      const data = await response.json();
      if (data.success) {
        this.localCache.messages = data.messages;
        this.saveLocalCache();
        return data.messages;
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
    return this.localCache.messages || [];
  }

  getMessages(filters?: { flowId?: string; contactId?: string; limit?: number }): Message[] {
    let messages = this.localCache.messages || [];
    
    if (filters?.flowId) {
      messages = messages.filter((m: Message) => m.flowId === filters.flowId);
    }
    if (filters?.contactId) {
      messages = messages.filter((m: Message) => m.contactId === filters.contactId);
    }
    if (filters?.limit) {
      messages = messages.slice(-filters.limit);
    }
    
    return messages;
  }

  createMessage(message: Omit<Message, 'id'>): Message {
    const newMessage: Message = {
      ...message,
      id: this.generateId('msg')
    };
    
    this.localCache.messages.push(newMessage);
    if (this.localCache.messages.length > 1000) {
      this.localCache.messages = this.localCache.messages.slice(-1000);
    }
    this.saveLocalCache();
    
    return newMessage;
  }

  // ============================================
  // CONTACT OPERATIONS
  // ============================================

  async getContactsAsync(): Promise<Contact[]> {
    try {
      const response = await fetch(`${this.API_BASE}/contacts`);
      const data = await response.json();
      if (data.success) {
        this.localCache.contacts = data.contacts;
        this.saveLocalCache();
        return data.contacts;
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
    return this.localCache.contacts || [];
  }

  getContacts(filters?: { tags?: string[]; limit?: number }): Contact[] {
    let contacts = this.localCache.contacts || [];
    
    if (filters?.tags && filters.tags.length > 0) {
      contacts = contacts.filter((c: Contact) =>
        filters.tags!.some(tag => c.tags?.includes(tag))
      );
    }
    if (filters?.limit) {
      contacts = contacts.slice(0, filters.limit);
    }
    
    return contacts;
  }

  getContact(contactId: string): Contact | null {
    return this.localCache.contacts?.find((c: Contact) => c.id === contactId) || null;
  }

  createContact(contact: Omit<Contact, 'id' | 'createdAt' | 'lastInteraction'>): Contact {
    const newContact: Contact = {
      ...contact,
      id: this.generateId('contact'),
      createdAt: new Date().toISOString(),
      lastInteraction: new Date().toISOString()
    };
    
    this.localCache.contacts.push(newContact);
    this.saveLocalCache();
    return newContact;
  }

  updateContact(contactId: string, updates: Partial<Contact>): Contact | null {
    const index = this.localCache.contacts.findIndex((c: Contact) => c.id === contactId);
    if (index === -1) return null;
    
    this.localCache.contacts[index] = {
      ...this.localCache.contacts[index],
      ...updates,
      lastInteraction: new Date().toISOString()
    };
    this.saveLocalCache();
    return this.localCache.contacts[index];
  }

  // ============================================
  // GROUP OPERATIONS
  // ============================================

  async getGroupsAsync(): Promise<Group[]> {
    try {
      const response = await fetch(`${this.API_BASE}/groups`);
      const data = await response.json();
      if (data.success) {
        this.localCache.groups = data.groups;
        this.saveLocalCache();
        return data.groups;
      }
    } catch (error) {
      console.error('Error fetching groups:', error);
    }
    return this.localCache.groups || [];
  }

  getGroups(): Group[] {
    this.getGroupsAsync();
    return this.localCache.groups || [];
  }

  createGroup(group: Omit<Group, 'id' | 'createdAt'>): Group {
    const newGroup: Group = {
      ...group,
      id: this.generateId('group'),
      createdAt: new Date().toISOString()
    };
    
    this.localCache.groups.push(newGroup);
    this.saveLocalCache();
    return newGroup;
  }

  addMemberToGroup(groupId: string, contactId: string): boolean {
    const group = this.localCache.groups.find((g: Group) => g.id === groupId);
    if (!group) return false;
    
    if (!group.members) group.members = [];
    if (group.members.includes(contactId)) return false;
    
    group.members.push(contactId);
    this.saveLocalCache();
    return true;
  }

  removeMemberFromGroup(groupId: string, contactId: string): boolean {
    const group = this.localCache.groups.find((g: Group) => g.id === groupId);
    if (!group || !group.members) return false;
    
    group.members = group.members.filter((m: string) => m !== contactId);
    this.saveLocalCache();
    return true;
  }

  // ============================================
  // ANALYTICS OPERATIONS
  // ============================================

  getAnalytics(): Analytics {
    return this.localCache.analytics || { daily: [], flowStats: {} };
  }

  recordFlowExecution(flowId: string, success: boolean, duration: number) {
    if (!this.localCache.analytics.flowStats[flowId]) {
      this.localCache.analytics.flowStats[flowId] = {
        totalExecutions: 0,
        successCount: 0,
        errorCount: 0,
        averageResponseTime: 0
      };
    }
    
    const stats = this.localCache.analytics.flowStats[flowId];
    stats.totalExecutions++;
    if (success) {
      stats.successCount++;
    } else {
      stats.errorCount++;
    }
    stats.averageResponseTime =
      (stats.averageResponseTime * (stats.totalExecutions - 1) + duration) / stats.totalExecutions;
    
    // Update flow stats
    const flow = this.localCache.flows?.find((f: Flow) => f.id === flowId);
    if (flow) {
      flow.stats.executions = stats.totalExecutions;
      flow.stats.successRate = (stats.successCount / stats.totalExecutions) * 100;
      flow.stats.lastRun = new Date().toISOString();
    }
    
    // Update daily analytics
    const today = new Date().toISOString().split('T')[0];
    let todayStats = this.localCache.analytics.daily.find((d: any) => d.date === today);
    
    if (!todayStats) {
      todayStats = {
        date: today,
        messagesProcessed: 0,
        flowExecutions: 0,
        newContacts: 0,
        errors: 0
      };
      this.localCache.analytics.daily.push(todayStats);
    }
    
    todayStats.flowExecutions++;
    if (!success) todayStats.errors++;
    
    this.saveLocalCache();
  }

  recordMessage() {
    const today = new Date().toISOString().split('T')[0];
    let todayStats = this.localCache.analytics.daily.find((d: any) => d.date === today);
    
    if (!todayStats) {
      todayStats = {
        date: today,
        messagesProcessed: 0,
        flowExecutions: 0,
        newContacts: 0,
        errors: 0
      };
      this.localCache.analytics.daily.push(todayStats);
    }
    
    todayStats.messagesProcessed++;
    this.saveLocalCache();
  }

  // ============================================
  // LOG OPERATIONS
  // ============================================

  async getLogsAsync(limit: number = 100, level?: string): Promise<Log[]> {
    try {
      let url = `/logs?limit=${limit}`;
      if (level) url += `&level=${level}`;
      const response = await fetch(url);
      const data = await response.json();
      if (data.success) {
        this.localCache.logs = data.logs;
        this.saveLocalCache();
        return data.logs;
      }
    } catch (error) {
      console.error('Error fetching logs:', error);
    }
    return this.localCache.logs || [];
  }

  createLog(log: { level: 'info' | 'warning' | 'error'; flowId?: string; message: string; details?: any }) {
    const newLog: Log = {
      id: this.generateId('log'),
      timestamp: new Date().toISOString(),
      ...log
    };
    
    this.localCache.logs.push(newLog);
    if (this.localCache.logs.length > 500) {
      this.localCache.logs = this.localCache.logs.slice(-500);
    }
    this.saveLocalCache();
  }

  getLogs(filters?: { flowId?: string; level?: string; limit?: number }): Log[] {
    let logs = this.localCache.logs || [];
    
    if (filters?.flowId) {
      logs = logs.filter((l: Log) => l.flowId === filters.flowId);
    }
    if (filters?.level) {
      logs = logs.filter((l: Log) => l.level === filters.level);
    }
    
    logs.sort((a: Log, b: Log) =>
      new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );
    
    if (filters?.limit) {
      logs = logs.slice(0, filters.limit);
    }
    
    return logs;
  }

  // ============================================
  // UTILITY OPERATIONS
  // ============================================

  exportDatabase(): string {
    return JSON.stringify(this.localCache, null, 2);
  }

  importDatabase(jsonString: string): boolean {
    try {
      const imported = JSON.parse(jsonString);
      this.localCache = imported;
      this.saveLocalCache();
      return true;
    } catch (error) {
      console.error('Failed to import database:', error);
      return false;
    }
  }

  resetDatabase() {
    this.localCache = {
      users: [],
      flows: [],
      messages: [],
      contacts: [],
      groups: [],
      logs: [],
      analytics: { daily: [], flowStats: {} }
    };
    this.saveLocalCache();
  }

  // Sync all data from backend
  async syncFromBackend() {
    await Promise.all([
      this.getAllFlowsAsync(),
      this.getMessagesAsync(),
      this.getContactsAsync(),
      this.getGroupsAsync(),
      this.getLogsAsync()
    ]);
  }
}

// Singleton instance
const dbService = new DatabaseService();
export default dbService;
